export type CashFlowFieldName =
  | 'purchasePrice'
  | 'initialRepairs'
  | 'closingCosts'
  | 'downPaymentPercent'
  | 'interestRate'
  | 'loanTermYears'
  | 'lenderFees'
  | 'monthlyRent'
  | 'otherIncome'
  | 'vacancyPercent'
  | 'annualTaxes'
  | 'annualInsurance'
  | 'monthlyHoa'
  | 'monthlyUtilities'
  | 'maintenancePercent'
  | 'managementPercent'
  | 'otherExpenses';

export type PurchaseMethod = 'mortgage' | 'cash';

export type CashFlowValues = Record<CashFlowFieldName, string>;

export type CashFlowErrors = Partial<Record<CashFlowFieldName, string>>;

export type CashFlowResult = {
  incomeAfterVacancy: number;
  operatingCosts: number;
  netOperatingIncome: number;
  mortgagePayment: number;
  monthlyCashFlow: number;
  annualCashFlow: number;
  cashInvested: number;
  cashOnCashReturn: number | undefined;
};

export type CashFlowCalculation = { ok: true; result: CashFlowResult } | { ok: false; errors: CashFlowErrors };

const FINANCING_FIELDS: Array<CashFlowFieldName> = [
  'downPaymentPercent',
  'interestRate',
  'loanTermYears',
  'lenderFees',
];

export const CURRENCY_FIELDS: Array<CashFlowFieldName> = [
  'purchasePrice',
  'initialRepairs',
  'closingCosts',
  'lenderFees',
  'monthlyRent',
  'otherIncome',
  'annualTaxes',
  'annualInsurance',
  'monthlyHoa',
  'monthlyUtilities',
  'otherExpenses',
];

const PERCENT_FIELDS: Array<CashFlowFieldName> = [
  'downPaymentPercent',
  'interestRate',
  'vacancyPercent',
  'maintenancePercent',
  'managementPercent',
];

const cleanNumber = (value: string) => value.replace(/[,\s$%]/g, '');

const isBlank = (value: string) => cleanNumber(value) === '';

export const parseNumber = (value: string): number | undefined => {
  const cleaned = cleanNumber(value);
  if (cleaned === '' || cleaned === '.') {
    return undefined;
  }
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const getMonthlyPayment = (principal: number, annualRatePercent: number, termYears: number) => {
  if (principal === 0) {
    return 0;
  }
  const payments = termYears * 12;
  const monthlyRate = annualRatePercent / 100 / 12;
  if (monthlyRate === 0) {
    return principal / payments;
  }
  return (principal * monthlyRate) / (1 - (1 + monthlyRate) ** -payments);
};

export const calculateCashFlow = (values: CashFlowValues, purchaseMethod: PurchaseMethod): CashFlowCalculation => {
  const isMortgage = purchaseMethod === 'mortgage';
  const errors: CashFlowErrors = {};
  const numbers = {} as Record<CashFlowFieldName, number>;

  (Object.keys(values) as Array<CashFlowFieldName>).forEach((field) => {
    if (!isMortgage && FINANCING_FIELDS.includes(field)) {
      numbers[field] = 0;
      return;
    }
    const parsed = parseNumber(values[field]);
    if (parsed === undefined) {
      if (!isBlank(values[field])) {
        errors[field] = 'Enter a valid number';
      }
      numbers[field] = 0;
      return;
    }
    if (parsed < 0) {
      errors[field] = 'Enter a number of 0 or more';
    } else if (PERCENT_FIELDS.includes(field) && parsed > 100) {
      errors[field] = 'Enter a percentage from 0 to 100';
    }
    numbers[field] = parsed;
  });

  if (!errors.purchasePrice && !(numbers.purchasePrice > 0)) {
    errors.purchasePrice = 'Enter a price greater than $0';
  }
  if (isMortgage && !errors.loanTermYears && !(numbers.loanTermYears > 0)) {
    errors.loanTermYears = 'Enter a loan term of at least 1 year';
  }

  if (Object.keys(errors).length > 0) {
    return { ok: false, errors };
  }

  const downPayment = isMortgage ? numbers.purchasePrice * (numbers.downPaymentPercent / 100) : numbers.purchasePrice;
  const loanAmount = numbers.purchasePrice - downPayment;
  const mortgagePayment = isMortgage ? getMonthlyPayment(loanAmount, numbers.interestRate, numbers.loanTermYears) : 0;

  const collectedRent = numbers.monthlyRent * (1 - numbers.vacancyPercent / 100);
  const incomeAfterVacancy = collectedRent + numbers.otherIncome;
  const operatingCosts =
    numbers.annualTaxes / 12 +
    numbers.annualInsurance / 12 +
    numbers.monthlyHoa +
    numbers.monthlyUtilities +
    numbers.monthlyRent * (numbers.maintenancePercent / 100) +
    collectedRent * (numbers.managementPercent / 100) +
    numbers.otherExpenses;
  const netOperatingIncome = incomeAfterVacancy - operatingCosts;
  const monthlyCashFlow = netOperatingIncome - mortgagePayment;
  const annualCashFlow = monthlyCashFlow * 12;
  const cashInvested = downPayment + numbers.closingCosts + numbers.initialRepairs + numbers.lenderFees;

  return {
    ok: true,
    result: {
      incomeAfterVacancy,
      operatingCosts,
      netOperatingIncome,
      mortgagePayment,
      monthlyCashFlow,
      annualCashFlow,
      cashInvested,
      cashOnCashReturn: cashInvested > 0 ? annualCashFlow / cashInvested : undefined,
    },
  };
};

export const formatCurrency = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export const formatPercent = (ratio: number) => `${(ratio * 100).toFixed(2)}%`;

export const formatInputValue = (value: string, isCurrency: boolean) => {
  const parsed = parseNumber(value);
  if (parsed === undefined) {
    return value.trim();
  }
  return isCurrency ? parsed.toLocaleString('en-US', { maximumFractionDigits: 2 }) : String(parsed);
};
